// SettingsSectionRail — left rail listing Settings sections (CHG-1239).
//
// Driven by the section vocabulary from specs/seeds/dashboard-v2-settings/sections.yaml
// (CHG-1224). Pure presentational — caller owns active state + onSelect.
//
// Per feedback_composition_system.md: slot composition, no business logic.

import {
  Building2,
  Palette,
  Globe,
  Layers,
  Shield,
  Scale,
  Sparkles,
  Plug,
  CreditCard,
  FileText,
  Wrench,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";
import type { SettingsSection } from "../../lib/settings-sections";

const ICON_MAP: Record<string, LucideIcon> = {
  Building2,
  Palette,
  Globe,
  Layers,
  Shield,
  Scale,
  Sparkles,
  Plug,
  CreditCard,
  FileText,
  Wrench,
};

export interface SettingsSectionRailProps {
  sections: SettingsSection[];
  activeSectionId: string;
  onSelect: (sectionId: string) => void;
}

export function SettingsSectionRail({ sections, activeSectionId, onSelect }: SettingsSectionRailProps) {
  return (
    <nav
      aria-label="Settings sections"
      style={{
        width: 240,
        flexShrink: 0,
        borderRight: "1px solid var(--color-border-subtle)",
        paddingRight: 12,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {sections.map((section) => {
        const Icon = ICON_MAP[section.icon] ?? SettingsIcon;
        const active = section.section_id === activeSectionId;
        return (
          <button
            key={section.section_id}
            type="button"
            data-section-id={section.section_id}
            aria-current={active ? "page" : undefined}
            onClick={() => onSelect(section.section_id)}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 12px 9px 16px",
              border: "none",
              background: active ? "var(--color-surface-hover)" : "transparent",
              borderRadius: "var(--radius-md)",
              cursor: "pointer",
              textAlign: "left",
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--color-text)" : "var(--color-text-secondary)",
              transition: "background 0.12s ease, color 0.12s ease",
            }}
          >
            {active ? (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 6,
                  bottom: 6,
                  width: 3,
                  borderRadius: 2,
                  background: "linear-gradient(180deg, var(--color-g-settings-from), var(--color-g-settings-to))",
                }}
              />
            ) : null}
            <Icon size={16} aria-hidden="true" />
            {section.label}
          </button>
        );
      })}
    </nav>
  );
}
